import { useSnackbar } from "../../../utils/notificacoes"
import React, { useCallback, useEffect, useReducer, useState } from "react"
import {
    atualizarPendentesFornecedor,
    cancelarCupomFiscal,
    obterCuponsFiscais,
    reimprimirCupomFiscal,
    sincronizarCuponsFornecedor,
} from "../../../services/emissaoCupomFiscal"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import type { Filtros } from "../models/emissaoCupomFiscalModel"
import { EmissaoCupomFiscalInicial } from "../models/emissaoCupomFiscalModel"
import { EmissaoCupomFiscalActionsTypes } from "./emissaoCupomFiscalActions"
import { EmissaoCupomFiscalContext } from "./EmissaoCupomFiscalContext"
import { emissaoCupomFiscalReducer } from "./emissaoCupomFiscalReducer"

export function EmissaoCupomFiscalContextProvider({ children }: { children: React.ReactNode }) {
    const [dados, dispatch] = useReducer(emissaoCupomFiscalReducer, EmissaoCupomFiscalInicial)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const carregarCupons = useCallback(
        async (filtros: Filtros) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return
            try {
                setLoading(true)
                const response = await obterCuponsFiscais(autorizacao, {
                    dataInicio: filtros.dataInicio,
                    dataFim: filtros.dataFim,
                    identificador: filtros.identificador || undefined,
                    numeroInicio: filtros.numeroInicio || undefined,
                    numeroFim: filtros.numeroFim || undefined,
                    tipo: filtros.tipo || undefined,
                })
                if ("retornoErro" in response) {
                    setErroCarregamento(response.mensagem || "Erro ao carregar")
                } else {
                    setErroCarregamento(null)
                    dispatch({ type: EmissaoCupomFiscalActionsTypes.CARREGAR, payload: response })
                }
            } catch {
                setErroCarregamento("Erro ao carregar cupons fiscais")
            } finally {
                setLoading(false)
            }
        },
        [setLoading]
    )

    const sincronizarCupons = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await sincronizarCuponsFornecedor(
                autorizacao,
                dados.filtros.dataInicio,
                dados.filtros.dataFim
            )
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao sincronizar", "error")
            } else {
                alertStart(response.mensagem, "info")
                await carregarCupons(dados.filtros)
            }
        } catch {
            alertStart("Erro ao sincronizar cupons", "error")
        } finally {
            setLoading(false)
        }
    }, [dados.filtros, carregarCupons, setLoading])

    const atualizarPendentes = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await atualizarPendentesFornecedor(
                autorizacao,
                dados.filtros.dataInicio,
                dados.filtros.dataFim
            )
            if ("retornoErro" in response) {
                alertStart(response.mensagem || "Erro ao atualizar pendentes", "error")
            } else {
                alertStart(response.mensagem, "success")
                await carregarCupons(dados.filtros)
            }
        } catch {
            alertStart("Erro ao atualizar pendentes", "error")
        } finally {
            setLoading(false)
        }
    }, [dados.filtros, carregarCupons, setLoading])

    const cancelarCupom = useCallback(
        async (idNfce: number) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return
            try {
                setLoading(true)
                const response = await cancelarCupomFiscal(autorizacao, idNfce)
                if ("retornoErro" in response) {
                    alertStart(response.mensagem || "Erro ao cancelar", "error")
                } else {
                    alertStart(response.mensagem, "info")
                    await carregarCupons(dados.filtros)
                }
            } catch {
                alertStart("Erro ao cancelar cupom", "error")
            } finally {
                setLoading(false)
            }
        },
        [dados.filtros, carregarCupons, setLoading]
    )

    const reimprimirCupom = useCallback(
        async (idNfce: number) => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return
            try {
                setLoading(true)
                const response = await reimprimirCupomFiscal(autorizacao, idNfce)
                if ("retornoErro" in response) {
                    alertStart(response.mensagem || "Erro ao reimprimir", "error")
                } else {
                    alertStart(response.mensagem, "info")
                }
            } catch {
                alertStart("Erro ao reimprimir cupom", "error")
            } finally {
                setLoading(false)
            }
        },
        [setLoading]
    )

    useEffect(() => {
        carregarCupons(dados.filtros)
    }, [])

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    return (
        <EmissaoCupomFiscalContext.Provider
            value={{
                dados,
                dispatch,
                erroCarregamento,
                carregarCupons,
                sincronizarCupons,
                atualizarPendentes,
                cancelarCupom,
                reimprimirCupom,
            }}
        >
            <Loading />
            {children}
        </EmissaoCupomFiscalContext.Provider>
    )
}
